import React, { useState, useRef, useEffect, useMemo } from 'react';
import LiveEditor from './LiveEditor';
import Footer from './Footer';
import TimelineChart, { TimelineEvent } from './TimelineChart';
import { fileToBase64, uploadMedia, getResizedImageUrl } from '../services/storageService';
import { isStandalone } from '../services/pwaService';
import { FIREBASE_STORAGE_BASE_PATH } from '../constants';
import { ArchiveFile } from '../types';

interface LegacyPageProps {
  isEditorMode: boolean;
  onSave: (key: string, html: string) => void;
  content: Record<string, string>;
  wikiArticles: ArchiveFile[];
  onSearchReadings?: (query: string) => void;
  onSearchChat?: (query: string) => void;
  onNavigateToCategory?: (category: string) => void;
  onNavigateToContact?: (subject?: string) => void;
  onNavigateToArticle?: (id: string) => void;
}

const LegacyPage: React.FC<LegacyPageProps> = ({ 
  isEditorMode, 
  onSave, 
  content, 
  wikiArticles,
  onSearchReadings, 
  onSearchChat, 
  onNavigateToCategory, 
  onNavigateToContact,
  onNavigateToArticle
}) => {
  // --- Component State ---
  const [isBioOpen, setIsBioOpen] = useState(false);
  const [heroImageError, setHeroImageError] = useState(false);
  const [researchImageError, setResearchImageError] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isPWA, setIsPWA] = useState(false);

  // Widget States
  const [activeWidget, setActiveWidget] = useState<'readings' | 'chat' | null>(null);
  const [widgetInput, setWidgetInput] = useState('');
  const widgetInputRef = useRef<HTMLInputElement>(null);

  // --- Media Modal State & Handlers ---
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [editingMediaKey, setEditingMediaKey] = useState('');
  const [mediaUrlInput, setMediaUrlInput] = useState('');
  const replaceFileInputRef = useRef<HTMLInputElement>(null);
  
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Parallax Refs
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const heroImageRef = useRef<HTMLImageElement>(null);

  // Speech State for Biography
  const [isSpeakingBio, setIsSpeakingBio] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

  // Detect PWA Mode & Mount Animation
  useEffect(() => {
      setIsMounted(true);
      
      const checkPWA = () => setIsPWA(isStandalone());
      checkPWA();
      
      const mediaQuery = window.matchMedia('(display-mode: standalone)');
      const handleMediaChange = (e: MediaQueryListEvent) => setIsPWA(e.matches);
      
      try {
        mediaQuery.addEventListener('change', handleMediaChange);
      } catch (e) {
        // Safari fallback
        mediaQuery.addListener(handleMediaChange);
      }

      const container = scrollContainerRef.current;
      const image = heroImageRef.current;
      
      if (!container || !image) return () => {
          try { mediaQuery.removeEventListener('change', handleMediaChange); } catch(e) { mediaQuery.removeListener(handleMediaChange); }
      };

      let rAFId: number;

      const handleScroll = () => {
          const scrollTop = container.scrollTop;
          cancelAnimationFrame(rAFId);
          rAFId = requestAnimationFrame(() => {
              if (image) {
                  image.style.transform = `translate3d(0, ${scrollTop * 0.5}px, 0)`;
              }
          });
      };

      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
          container.removeEventListener('scroll', handleScroll);
          cancelAnimationFrame(rAFId);
          try { mediaQuery.removeEventListener('change', handleMediaChange); } catch(e) { mediaQuery.removeListener(handleMediaChange); }
      };
  }, []);

  // Load Voices
  useEffect(() => {
    const updateVoices = () => {
        setVoices(window.speechSynthesis.getVoices());
    };
    updateVoices();
    window.speechSynthesis.onvoiceschanged = updateVoices;
    return () => {
        window.speechSynthesis.cancel();
    };
  }, []);

  // Focus input when widget popover opens
  useEffect(() => {
      if (activeWidget && widgetInputRef.current) {
          // Small delay for animation
          setTimeout(() => widgetInputRef.current?.focus(), 100);
      } else {
          setWidgetInput('');
      }
  }, [activeWidget]);
  
  // Define default content using Firebase Storage paths for images
  const defaults = {
    legacy_hero_title: "Edgar Cayce's Legacy",
    legacy_hero_image: `${FIREBASE_STORAGE_BASE_PATH}legacy_pages/hero.webp`, // Default to uploaded image
    legacy_bio_subtitle: "America's Greatest Psychic and Clairvoyant",
    legacy_bio_title: "Edgar Cayce's Biography",
    legacy_bio_body: `<p>Edgar Cayce (1877-1945), known as the "Sleeping Prophet," gave over 14,000 readings on subjects ranging from health and holistic medicine to reincarnation, ancient mysteries, and future prophecies. For forty-three years of his adult life, he had the ability to put himself into some kind of self-induced sleep state by lying down on a couch, closing his eyes, and folding his hands over his stomach. This state of relaxation and meditation enabled him to place his mind in contact with all time and space — the universal consciousness.</p><p>Cayce was born on a farm in Hopkinsville, Kentucky, in 1877, and his psychic abilities began to appear as early as his childhood. He was able to see and talk to his late grandfather's spirit, and often played with "imaginary friends" whom he said were spirits on the other side. He also displayed an uncanny ability to memorize the pages of a book simply by sleeping on it.</p><p>The majority of Cayce's readings deal with holistic health and the treatment of illness. As early as 1901, Cayce was describing the importance of diet, meditation, and exercise. But perhaps the most fascinating aspect of his work is the information concerning the history of the human soul, specifically reincarnation and karma.</p><p>The readings explain that our life today is the result of what we have done in the past. As the Bible states: "Be not deceived; God is not mocked: for whatsoever a man soweth, that shall he also reap." (Galatians 6:7). Cayce's readings bring this concept to life, explaining how our current relationships, talents, and challenges are often the result of actions and choices made in previous lifetimes.</p>`,
    legacy_health_title: "Health Advice",
    legacy_health_subtitle: "Are you ready to explore the secrets of holistic health?",
    legacy_health_body: `According to Edgar Cayce, there is no incurable condition that can defy the power of nature. <span class="font-bold">Purification</span> and <span class="font-bold">regeneration</span> play a central role in this. Vitalization is based on a balanced diet, keeping the body alkaline, and understanding that the physical body is a temple of the living spirit.`,
    legacy_spiritual_title: "Spiritual Teachings",
    legacy_spiritual_body: `Edgar Cayce became popular for his precise descriptions of past life regressions and their consequences for the present. The readings consistently emphasize that the purpose of life is to bring the spiritual into the material — "To know yourself to be yourself and yet one with God."`,
    legacy_research_image: `${FIREBASE_STORAGE_BASE_PATH}legacy_pages/research.webp`, // Default to uploaded image
    legacy_timeline_data: '',
    legacy_timeline_row_titles: '',
  };
  
  const pageContent = { ...defaults, ...content };

  // Reset error states when content changes (user selects new image)
  useEffect(() => {
      setHeroImageError(false);
  }, [pageContent.legacy_hero_image]);

  useEffect(() => {
      setResearchImageError(false);
  }, [pageContent.legacy_research_image]);

  const openMediaModal = (key: string) => {
    setEditingMediaKey(key);
    setIsMediaModalOpen(true);
    setMediaUrlInput('');
    setUploadProgress(0);
    setUploading(false);
  };

  const closeMediaModal = () => {
    if (uploading) return;
    setIsMediaModalOpen(false);
    setEditingMediaKey('');
  };

  const handleReplaceMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && editingMediaKey) {
        setUploading(true);
        setUploadProgress(0);
        
        try {
            // Upload to cloud storage to get a permanent URL for user uploads
            const path = `${FIREBASE_STORAGE_BASE_PATH}legacy_pages/${editingMediaKey}_${Date.now()}_${file.name}`;
            const storagePath = await uploadMedia(file, path, (progress) => setUploadProgress(progress));
            
            // Save the storage path
            onSave(editingMediaKey, storagePath);
            closeMediaModal(); 
        } catch (error) {
            console.warn("Image upload failed", error);
            // Fallback to local base64 if upload fails (e.g. offline)
            try {
                const localUrl = await fileToBase64(file);
                onSave(editingMediaKey, localUrl); // Save base64 for temporary display
                closeMediaModal();
            } catch (e) {
                console.error("Local fallback failed", e);
            }
        } finally {
            setUploading(false);
        }
    }
    if(e.target) e.target.value = ''; // Reset file input
  };
  
  const getYouTubeId = (url: string): string | null => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const handleAssetSelect = (assetStoragePath: string) => {
      if (!editingMediaKey) return;
      onSave(editingMediaKey, assetStoragePath);
      closeMediaModal();
  };

  const handleApplyUrl = () => {
    if (!mediaUrlInput.trim() || !editingMediaKey) return;
    const url = mediaUrlInput.trim();
    const youtubeId = getYouTubeId(url);
    const isImageUrl = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url);
    
    let finalUrl = url;
    if (youtubeId) {
      finalUrl = `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`; // YouTube thumbnail directly
    } else if (!isImageUrl) {
      alert("Unsupported URL. Please use a direct image link (e.g., .jpg, .png) or a YouTube URL.");
      return;
    }
    
    // Save the direct URL (external) or the YouTube thumbnail URL.
    // If it's a storage path, getResizedImageUrl will handle it.
    onSave(editingMediaKey, finalUrl); 
    closeMediaModal();
  };

  const handleWidgetSubmit = () => {
      if (!widgetInput.trim()) return;
      
      if (activeWidget === 'readings' && onSearchReadings) {
          onSearchReadings(widgetInput);
      } else if (activeWidget === 'chat' && onSearchChat) {
          onSearchChat(widgetInput);
      }
      setActiveWidget(null);
  };

  const normalizeTextForSpeech = (text: string): string => {
      if (!text) return '';
      // 1. Fix reading numbers: 0065-0003 -> 65-3
      let normalized = text.replace(/\b0+(\d+)-0+(\d+)\b/g, '$1-$2');
      normalized = normalized.replace(/\b0+([1-9]\d*)\b/g, '$1'); // 007 -> 7
      
      // 2. Fix all-caps words (2+ chars) to lowercase to prevent spelling out (e.g. "IN", "THE")
      normalized = normalized.replace(/\b([A-Z]{2,})\b/g, (match) => {
          return match.toLowerCase();
      });
      return normalized;
  };

  const handleBioSpeak = () => {
      if (isSpeakingBio) {
          window.speechSynthesis.cancel();
          setIsSpeakingBio(false);
          return;
      }

      // Fetch from DOM to get translated text
      const bioEl = document.getElementById('legacy-bio-content');
      let text = '';
      if (bioEl && bioEl.innerText) {
          text = bioEl.innerText;
      } else {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = pageContent.legacy_bio_body;
          text = tempDiv.innerText;
      }

      if (!text.trim()) return;
      
      text = normalizeTextForSpeech(text);

      const utterance = new SpeechSynthesisUtterance(text);
      
      // Attempt to find a good voice
      let currentLang = document.documentElement.lang || 'en';
      const match = document.cookie.match(/googtrans=\/auto\/([a-zA-Z-]+)/);
      if (match && match[1]) {
          currentLang = match[1];
      }
      utterance.lang = currentLang;
      
      const matchingVoices = voices.filter(v => v.lang.startsWith(currentLang.split('-')[0]));
      // Sort: Microsoft > Google, Natural/Online > Standard
      matchingVoices.sort((a, b) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();
          const scoreA = (aName.includes('microsoft') ? 2 : 0) + (aName.includes('natural') ? 1 : 0) + (aName.includes('online') ? 0.5 : 0);
          const scoreB = (bName.includes('microsoft') ? 2 : 0) + (bName.includes('natural') ? 1 : 0) + (bName.includes('online') ? 0.5 : 0);
          return scoreB - scoreA;
      });
      
      if (matchingVoices.length > 0) utterance.voice = matchingVoices[0];

      utterance.onend = () => setIsSpeakingBio(false);
      utterance.onerror = () => setIsSpeakingBio(false);

      window.speechSynthesis.speak(utterance);
      setIsSpeakingBio(true);
  };

  // Helper to generate srcSet for high resolution support
  const getResponsiveSrcSet = (path: string) => {
      // If path is external (http) or data uri, we can't resize it using our service
      if (path.startsWith('http') && !path.includes('firebasestorage')) return undefined;
      if (path.startsWith('data:')) return undefined;

      // By claiming 'original' is 3840w (4K), we ensure high-DPI and large desktops prefer it
      // over the 1800w version, preventing upscaling artifacts of the compressed version.
      return `
        ${getResizedImageUrl(path, '1000x1000')} 1000w,
        ${getResizedImageUrl(path, '1800x1800')} 1800w,
        ${getResizedImageUrl(path, 'original')} 3840w
      `;
  };

  // Helper to get the best default src (high res)
  const getHighResSrc = (path: string) => {
      return getResizedImageUrl(path, 'original');
  };

  const timelineEvents = useMemo(() => {
    try {
        if (pageContent.legacy_timeline_data) {
            return JSON.parse(pageContent.legacy_timeline_data) as TimelineEvent[];
        }
    } catch (e) {
        console.error("Failed to parse timeline data", e);
    }
    return [] as TimelineEvent[];
  }, [pageContent.legacy_timeline_data]);

  const timelineRowTitles = useMemo(() => {
    try {
        if (pageContent.legacy_timeline_row_titles) {
            return JSON.parse(pageContent.legacy_timeline_row_titles) as string[];
        }
    } catch (e) {
        console.error("Failed to parse timeline row titles", e);
    }
    return [] as string[];
  }, [pageContent.legacy_timeline_row_titles]);

  return (
    <>
      <input type="file" ref={replaceFileInputRef} className="hidden" accept="image/*" onChange={handleReplaceMediaUpload} />
      
      {/* Media Modal */}
      {isMediaModalOpen && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center pt-0 px-4 pb-4" onClick={closeMediaModal}>
            <div className="bg-white dark:bg-stone-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-stone-100 dark:border-stone-700 flex justify-between items-center bg-stone-50 dark:bg-stone-900/50">
                    <h3 className="font-bold text-stone-800 dark:text-stone-200 flex items-center gap-2">
                        <i className="fa-solid fa-camera text-amber-500"></i>
                        Change Media
                    </h3>
                    {!uploading && (
                        <button onClick={closeMediaModal} className="text-stone-400 hover:text-stone-600">
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    )}
                </div>
                <div className="p-6 space-y-6">
                    {/* Asset Gallery - Removed static ASSET_IMAGES, only show limited default icons or be empty */}
                    <div>
                        <label className="text-xs font-bold text-stone-400 uppercase tracking-wider block mb-2">Select from Default Icons</label>
                        <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto custom-scrollbar">
                            {[
                                `${FIREBASE_STORAGE_BASE_PATH}app_branding/hero.webp`,
                                `${FIREBASE_STORAGE_BASE_PATH}app_branding/research.webp`,
                                `${FIREBASE_STORAGE_BASE_PATH}app_branding/logo.webp`,
                                `${FIREBASE_STORAGE_BASE_PATH}app_branding/sphinx.webp`,
                                `${FIREBASE_STORAGE_BASE_PATH}app_branding/pyramids.webp`,
                                `${FIREBASE_STORAGE_BASE_PATH}app_branding/Covenant.webp`,
                                `${FIREBASE_STORAGE_BASE_PATH}app_branding/Nikola Tesla.webp`
                            ].map((path, idx) => (
                                <button 
                                    key={idx} 
                                    onClick={() => handleAssetSelect(path)} 
                                    className="relative aspect-video rounded-lg overflow-hidden border border-stone-200 dark:border-stone-700 hover:border-amber-500 transition-all group bg-stone-100 dark:bg-stone-900"
                                >
                                    {/* Use a medium size for selection thumbnails */}
                                    <img src={getResizedImageUrl(path, '300x300')} alt="Asset" className="w-full h-full object-cover" onError={(e) => e.currentTarget.style.display = 'none'} />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] truncate px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {path.split('/').pop()}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <hr className="flex-1 border-stone-200 dark:border-stone-600" />
                        <span className="text-xs font-bold text-stone-400">OR</span>
                        <hr className="flex-1 border-stone-200 dark:border-stone-600" />
                    </div>

                    <div>
                        <label className="text-xs font-bold text-stone-400 uppercase tracking-wider">Upload from device</label>
                        <button 
                            onClick={() => replaceFileInputRef.current?.click()}
                            disabled={uploading}
                            className={`mt-2 w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold border transition-all relative overflow-hidden ${
                                uploading 
                                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500 text-amber-700 dark:text-amber-400' 
                                : 'bg-stone-100 dark:bg-stone-700 hover:bg-stone-200 dark:hover:bg-stone-600 text-stone-700 dark:text-stone-300 border-stone-200 dark:border-stone-600'
                            }`}
                        >
                            <i className={`fa-solid ${uploading ? 'fa-circle-notch animate-spin' : 'fa-upload'}`}></i>
                            {uploading ? `Processing...` : 'Choose an image...'}
                        </button>
                    </div>
                    
                    <div className="flex items-center gap-4">
                        <hr className="flex-1 border-stone-200 dark:border-stone-600" />
                        <span className="text-xs font-bold text-stone-400">OR</span>
                        <hr className="flex-1 border-stone-200 dark:border-stone-600" />
                    </div>

                    <div>
                        <label className="text-xs font-bold text-stone-400 uppercase tracking-wider">Paste an image link</label>
                        <div className="mt-2 flex gap-2">
                            <input 
                                type="text" 
                                value={mediaUrlInput}
                                onChange={(e) => setMediaUrlInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleApplyUrl()}
                                placeholder="Image or YouTube URL..."
                                disabled={uploading}
                                className="flex-1 bg-stone-100 dark:bg-stone-900 border border-stone-200 dark:border-stone-600 rounded-lg p-3 text-sm focus:border-amber-500 focus:ring-amber-500 outline-none text-stone-800 dark:text-stone-200"
                            />
                            <button 
                                onClick={handleApplyUrl}
                                disabled={uploading}
                                className="px-4 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-bold shadow-sm transition-colors disabled:opacity-50"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      )}

      {/* Widget Search Popover */}
      {activeWidget && (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start pt-0 justify-center px-4 pb-4 animate-in fade-in duration-200" onClick={() => setActiveWidget(null)}>
              <div className="w-full max-w-2xl bg-white dark:bg-stone-800 rounded-2xl shadow-2xl p-2 transform transition-all scale-100 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-2 p-2">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${activeWidget === 'readings' ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-indigo-100 dark:bg-indigo-900/30'}`}>
                            <i className={`fa-solid ${activeWidget === 'readings' ? 'fa-scroll text-amber-600 dark:text-amber-500' : 'fa-wand-magic-sparkles text-indigo-600 dark:text-indigo-400'}`}></i>
                        </div>
                        <input 
                            ref={widgetInputRef}
                            type="text" 
                            placeholder={activeWidget === 'readings' ? "Search Readings..." : "Ask the Librarian..."}
                            value={widgetInput}
                            onChange={(e) => setWidgetInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleWidgetSubmit(); }}
                            className="flex-1 bg-transparent border-none outline-none px-2 text-lg font-medium text-stone-800 dark:text-stone-100 placeholder:text-stone-400"
                        />
                        <button 
                            onClick={handleWidgetSubmit}
                            className="w-10 h-10 rounded-xl bg-stone-100 dark:bg-stone-700 hover:bg-amber-500 hover:text-white dark:hover:bg-amber-600 text-stone-500 dark:text-stone-300 transition-colors flex items-center justify-center"
                        >
                            <i className="fa-solid fa-arrow-right"></i>
                        </button>
                  </div>
              </div>
          </div>
      )}

      <div ref={scrollContainerRef} className="w-full h-full overflow-y-auto overflow-x-hidden custom-scrollbar bg-[#e6dec6] font-sans text-stone-800 relative">
        
        {/* Hero Section */}
        <div className="relative h-[70vh] min-h-[500px] w-full group bg-stone-900 overflow-hidden">
          <img 
              ref={heroImageRef}
              src={!heroImageError 
                  ? getHighResSrc(pageContent.legacy_hero_image) 
                  : getHighResSrc(defaults.legacy_hero_image)
              }
              srcSet={!heroImageError 
                  ? getResponsiveSrcSet(pageContent.legacy_hero_image) 
                  : getResponsiveSrcSet(defaults.legacy_hero_image)
              }
              sizes="100vw"
              alt="Cosmic Connection" 
              className="w-full h-[120%] object-cover object-center absolute -top-[10%] left-0 transition-opacity duration-500 will-change-transform"
              onError={() => setHeroImageError(true)}
              style={{ willChange: 'transform' }}
          />
          {isEditorMode && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-4 flex items-start justify-end z-10">
                  <button
                      onClick={() => openMediaModal('legacy_hero_image')}
                      className="w-10 h-10 bg-white/20 backdrop-blur text-white rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                      title="Change Media"
                  >
                      <i className="fa-solid fa-camera"></i>
                  </button>
              </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/30 via-transparent to-transparent"></div>
          <div className="absolute inset-0 flex items-center justify-center pt-20">
              <LiveEditor
                isEditorMode={isEditorMode}
                onSave={onSave}
                contentKey="legacy_hero_title"
                initialHtml={pageContent.legacy_hero_title}
                tagName="h1"
                className={`text-5xl md:text-8xl font-serif text-white font-normal drop-shadow-2xl text-center px-4 tracking-wide transition-all duration-1000 ease-out transform ${isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-12'}`}
                placeholder="Hero Title"
              />
          </div>
        </div>

        {/* Biography Section */}
        <div className="py-20 bg-[#e6dec6]">
          <div className="max-w-6xl mx-auto px-6 mb-20 relative">
            <TimelineChart 
                items={timelineEvents}
                articles={wikiArticles}
                isEditorMode={isEditorMode}
                onNavigate={(articleId) => onNavigateToArticle?.(articleId)}
                onUpdate={(newItems) => onSave('legacy_timeline_data', JSON.stringify(newItems))}
                rowTitles={timelineRowTitles}
                onUpdateRowTitles={(newTitles) => onSave('legacy_timeline_row_titles', JSON.stringify(newTitles))}
            />
          </div>

          <div className="text-center">
            <LiveEditor
              isEditorMode={isEditorMode}
              onSave={onSave}
              contentKey="legacy_bio_subtitle"
              initialHtml={pageContent.legacy_bio_subtitle}
              tagName="h2"
              className={`text-xl md:text-3xl font-bold text-[#1e2a4a] mb-6 tracking-wide text-opacity-90 transition-all duration-700 delay-300 transform ${isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              placeholder="Subtitle"
            />
            <button onClick={() => setIsBioOpen(!isBioOpen)} className={`inline-flex flex-col items-center justify-center gap-2 group cursor-pointer transition-all duration-700 delay-500 transform ${isMounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <LiveEditor
                isEditorMode={isEditorMode}
                onSave={onSave}
                contentKey="legacy_bio_title"
                initialHtml={pageContent.legacy_bio_title}
                tagName="h3"
                className="text-5xl md:text-6xl font-serif text-stone-900 font-normal"
                placeholder="Main Title"
              />
              <i className={`fa-solid fa-chevron-down text-4xl text-black mt-4 transition-transform duration-300 ${isBioOpen ? 'rotate-180' : ''}`}></i>
            </button>
            
            <div className={`transition-all duration-500 ease-in-out max-w-3xl mx-auto text-left overflow-hidden ${isBioOpen ? 'max-h-[2000px] opacity-100 mt-12' : 'max-h-0 opacity-0'}`}>
              <div id="legacy-bio-content" className="bg-white dark:bg-stone-800 border dark:border-stone-700 text-stone-800 dark:text-stone-200 rounded-2xl rounded-tl-none font-serif leading-relaxed text-lg shadow-sm relative group p-6 md:p-8">
                   <LiveEditor
                      isEditorMode={isEditorMode}
                      onSave={onSave}
                      contentKey="legacy_bio_body"
                      initialHtml={pageContent.legacy_bio_body}
                      tagName="div"
                      className="prose prose-stone dark:prose-invert max-w-none text-base leading-relaxed"
                      placeholder="Biography content..."
                  />
                  <div className="absolute bottom-4 right-4 flex items-center gap-1 bg-stone-50 dark:bg-stone-900/50 backdrop-blur rounded-lg border border-stone-100 dark:border-stone-700 p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-sm">
                      <button onClick={handleBioSpeak} className={`w-7 h-7 flex items-center justify-center rounded hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors ${isSpeakingBio ? 'text-amber-500 animate-pulse' : 'text-stone-400 dark:text-stone-500'}`} title={isSpeakingBio ? "Stop" : "Read Aloud"}><i className={`fa-solid ${isSpeakingBio ? 'fa-stop' : 'fa-volume-high'} text-xs`}></i></button>
                      <div className="w-px h-3 bg-stone-200 dark:bg-stone-700"></div>
                      <button onClick={() => { const tempDiv = document.createElement('div'); tempDiv.innerHTML = pageContent.legacy_bio_body; navigator.clipboard.writeText(tempDiv.innerText); }} className="w-7 h-7 flex items-center justify-center rounded hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-400 dark:text-stone-500 transition-colors" title="Copy"><i className="fa-regular fa-copy text-xs"></i></button>
                  </div>
              </div>
            </div>
          </div>
        </div>

        {/* Two Column Section */}
        <div className="flex flex-col lg:flex-row w-full">
          <div className="flex-1 bg-[#6a8a4f] p-16 md:p-24 text-center text-white flex flex-col items-center justify-center min-h-[600px]">
            <LiveEditor
              isEditorMode={isEditorMode}
              onSave={onSave}
              contentKey="legacy_health_title"
              initialHtml={pageContent.legacy_health_title}
              tagName="h3"
              className="text-4xl md:text-5xl font-serif mb-10 font-normal"
              placeholder="Section Title"
            />
            <LiveEditor
              isEditorMode={isEditorMode}
              onSave={onSave}
              contentKey="legacy_health_subtitle"
              initialHtml={pageContent.legacy_health_subtitle}
              tagName="p"
              className="max-w-xl text-xl leading-relaxed mb-6 font-light"
              placeholder="Section Subtitle"
            />
            <LiveEditor
              isEditorMode={isEditorMode}
              onSave={onSave}
              contentKey="legacy_health_body"
              initialHtml={pageContent.legacy_health_body}
              tagName="p"
              className="max-w-xl text-base leading-relaxed opacity-90 mb-12 font-light"
              placeholder="Section Body Text"
            />
            <button className="text-yellow-400 text-xs font-bold uppercase tracking-widest hover:text-white transition-colors border-b border-transparent hover:border-white pb-1">Read More ...</button>
          </div>
          <div className="flex-1 bg-[#0f152b] p-16 md:p-24 text-center text-white flex flex-col items-center justify-center min-h-[600px]">
            <LiveEditor
              isEditorMode={isEditorMode}
              onSave={onSave}
              contentKey="legacy_spiritual_title"
              initialHtml={pageContent.legacy_spiritual_title}
              tagName="h3"
              className="text-4xl md:text-5xl font-serif mb-10 font-normal"
              placeholder="Section Title"
            />
            <LiveEditor
              isEditorMode={isEditorMode}
              onSave={onSave}
              contentKey="legacy_spiritual_body"
              initialHtml={pageContent.legacy_spiritual_body}
              tagName="p"
              className="max-w-xl text-base leading-relaxed opacity-90 mb-12 font-light"
              placeholder="Section Body Text"
            />
            <button className="text-orange-400 text-xs font-bold uppercase tracking-widest hover:text-white transition-colors border-b border-transparent hover:border-white pb-1">Read More ...</button>
          </div>
        </div>

        {/* Research Collaboration */}
        <div className="relative h-[500px] w-full overflow-hidden group">
            <img 
              src={!researchImageError 
                  ? getHighResSrc(pageContent.legacy_research_image) 
                  : getHighResSrc(defaults.legacy_research_image)
              }
              srcSet={!researchImageError 
                  ? getResponsiveSrcSet(pageContent.legacy_research_image) 
                  : getResponsiveSrcSet(defaults.legacy_research_image)
              }
              sizes="100vw"
              alt="Research Library" 
              className="absolute inset-0 w-full h-full object-cover brightness-75"
              onError={() => setResearchImageError(true)}
            />
            {isEditorMode && (
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity p-4 flex items-start justify-end z-10">
                  <button onClick={() => openMediaModal('legacy_research_image')} className="w-10 h-10 bg-white/20 backdrop-blur text-white rounded-full flex items-center justify-center hover:bg-white/30 transition-colors" title="Change Media"><i className="fa-solid fa-camera"></i></button>
              </div>
          )}
            <div className="absolute inset-0 flex items-center justify-center bg-black/10">
              <div className="bg-[#ed9f3d] py-12 px-8 md:px-16 max-w-4xl w-[90%] text-center shadow-lg relative top-10 flex flex-col items-center">
                  <h2 className="text-3xl md:text-5xl font-serif text-[#1e2a4a] mb-6 font-normal">Research Collaboration</h2>
                  <p className="text-[#1e2a4a] text-lg leading-relaxed mb-8 max-w-2xl font-medium">We are establishing a global research cooperative to build the definitive Akashic Wiki. This project aims to index, tag, and cross-reference the 14,306 Edgar Cayce readings with modern scientific, historical, and medical data. We invite qualified researchers, archivists, and developers to apply for contributor access.</p>
                  <button onClick={(e) => { e.preventDefault(); window.location.href = `mailto:${'info'}@${'edgarcayce.ch'}?subject=Collaboration`; }} className="relative z-10 bg-[#1e2a4a] text-[#ed9f3d] px-8 py-3 rounded-full font-bold uppercase tracking-widest text-xs hover:bg-stone-900 transition-colors shadow-md inline-block">Become a Collaborator</button>
              </div>
            </div>
        </div>
        
        <div className="bg-[#ed9f3d] h-20 w-full md:hidden"></div>
        <Footer />
      </div>

      {isEditorMode && (
          <div className="hidden">
              {/* Force mock update if needed via parent command */}
              <button onClick={() => {
                  // These are now storage paths that get converted by getResizedImageUrl in LegacyPage
                  onSave('legacy_hero_image', `${FIREBASE_STORAGE_BASE_PATH}legacy_pages/hero.webp`);
                  onSave('legacy_research_image', `${FIREBASE_STORAGE_BASE_PATH}legacy_pages/research.webp`);
                  onSave('app_logo', `${FIREBASE_STORAGE_BASE_PATH}app_branding/logo.webp`);
              }} id="btn-update-mocks-legacy"></button>
          </div>
      )}
    </>
  );
};

export default LegacyPage;
